// View fragments shared across screens, ported from glasshouse's fragment
// helpers. Same class names, same structure.
import { Fragment, useState, type CSSProperties, type ReactNode } from "react";
import { readBurden, type BurdenFields } from "../../shared/burden.js";
import { CHIP_CLASS } from "../copy.js";
import type { ExpertLite } from "../state/api.js";
import { useStore } from "../state/store.js";
import type { CardView } from "../state/types.js";
import { Avatar, avatarTitle } from "./Avatar.js";
import { artFor } from "./placeholderArt.js";
import { artCss } from "../assets.js";

export function EvChip({ chip }: { chip: string }) {
  return <span className={`evc ${CHIP_CLASS[chip] ?? "viewpoint"}`}>{chip}</span>;
}

// The burden line as metadata-line parts: two canonical facts, the time and
// the money, from the parser rather than from wherever the dossier happened to
// put them (29 Jul 2026, replacing a split on the dots). The notes are the
// modal's alone: "requires a blender" is worth a sentence and too long for a
// row.
export function burdenParts(burden: BurdenFields | null, withNotes = false): string[] {
  const b = readBurden(burden);
  return [b.time, b.money, ...(withNotes ? b.notes : [])].filter(Boolean);
}

// Metadata as one dot-separated text line that wraps (29 Jul 2026): the
// rows of pills read as buttons, could not wrap, and ate width. Falsy
// parts drop out, so callers pass conditionals straight in.
export function MetaLine({ parts, className, style }: {
  parts: ReactNode[];
  className?: string;
  style?: CSSProperties;
}) {
  const shown = parts.filter(Boolean);
  return (
    <div className={className ? `metaline ${className}` : "metaline"} style={style}>
      {shown.map((p, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="mdot"> · </span>}
          {p}
        </Fragment>
      ))}
    </div>
  );
}

// Where the evidence chip would sit on a practice the user created: their
// own practice is not a claim, so it gets no evidence label (and no fifth
// label was invented).
export function YoursChip() {
  return <span className="evc yours">Yours</span>;
}

// The avatar for the user's own practice rows: no expert, no portrait
// claim, just a quiet marker on the neutral fallback colour.
export const YOURS_AVATAR = { name: "You", initials: "You", color: "#5A5F72" };

// A long list shows one screenful and hands the rest to a Load more link
// (29 Jul 2026; an auto-loading variant was built and cut the same day:
// plain paging is predictable and the footer stays reachable). Key this
// from the caller's filter state so changing filters starts back at one
// page.
export function PagedList({ children, batch, first, className }: {
  children: ReactNode[];
  batch: number;
  // The opening screenful, when it differs from the per-click batch.
  first?: number;
  className?: string;
}) {
  const [pages, setPages] = useState(0);
  const shown = children.slice(0, (first ?? batch) + pages * batch);
  const remaining = children.length - shown.length;
  return (
    <>
      <div className={className}>{shown}</div>
      {remaining > 0 && (
        <button
          className="btn"
          style={{ display: "block", margin: "4px auto 0" }}
          onClick={() => setPages((p) => p + 1)}
        >
          Load more ({remaining})
        </button>
      )}
    </>
  );
}

// Full roster entry for an expert id: avatar fields live on the roster
// list, names also in the state payload.
export function useExpert(id: string): ExpertLite {
  const experts = useStore((s) => s.experts);
  return (
    experts?.find((e) => e.id === id) ?? {
      id,
      kind: "person",
      name: id,
      short: id,
      expertType: null,
      role: null,
      lens: null,
      status: "candidate",
      cat: null,
      cats: [],
      reach: null,
      topicLabels: null,
      photo: null,
      photoPos: null,
      photoBy: null,
      photoLicense: null,
      photoLicenseUrl: null,
      photoPage: null,
      initials: "??",
      color: "#5A5F72",
      claimed: false,
      cardCount: 0,
      feedCount: 0,
    }
  );
}

function capWords(s: string, n: number): string {
  const words = s.split(/\s+/).filter(Boolean);
  // A cut line never ends on a comma before the ellipsis ("Anatomy,…").
  return words.length <= n ? s : `${words.slice(0, n).join(" ").replace(/[,;:]+$/, "")}…`;
}

// Display caps for expert descriptors (28 Jul 2026): `role` is a verified
// credentials record (licence numbers and all) and `lens` a thesis
// sentence; neither was written to be a one-liner. Every surface except the
// full profile shows these trimmed forms instead.

// Who they are: the credential token plus what they actually do.
// "D.C. (Life University, 1995) · not a physician, no active licence ·
// health educator and programme founder" reads "D.C. · health educator and
// programme founder": parentheticals dropped per segment, disclaimer
// segments ("no ...", "not ...") skipped when something descriptive exists.
export function roleLine(e: {
  role?: string | null;
  lens?: string | null;
  cat?: string | null;
  expertType?: string | null;
}): string {
  const segments = (e.role ?? "")
    .replace(/\([^)]*\)/g, "")
    .split("·")
    .map((p) => p.trim().replace(/\s+/g, " "))
    .filter(Boolean);
  const isCredential = (s: string) => s.length <= 12 && /[A-Z]/.test(s) && !/\s[a-z]+\s/.test(` ${s} `);
  const isDisclaimer = (s: string) => /^(no|not|retired from)\b/i.test(s);
  const credential = segments[0] && isCredential(segments[0]) ? segments[0] : null;
  const descriptive =
    segments.find((s) => !isCredential(s) && !isDisclaimer(s)) ??
    segments.find((s) => !isCredential(s));
  // 9 tokens, not 10 (29 Jul 2026): at 10 the cut lands on an orphan like
  // "UC…" and the line wraps to a third row on the Follow tile.
  if (credential && descriptive) return capWords(`${credential} · ${descriptive}`, 9);
  if (descriptive) return capWords(descriptive, 9);
  if (credential) {
    // A bare degree describes nobody; say what they do instead.
    const kind = e.expertType ? e.expertType.charAt(0).toUpperCase() + e.expertType.slice(1) : null;
    return kind ? `${credential} · ${kind}` : credential;
  }
  return capWords((e.lens ?? e.cat ?? "").split(";")[0]?.trim() ?? "", 12);
}

// What they are about: the lens up to its first clause break, word-capped.
export function lensLine(e: { lens?: string | null; cat?: string | null }): string {
  const lens = (e.lens ?? "").split(";")[0]?.trim() ?? "";
  return lens ? capWords(lens, 12) : (e.cat ?? "");
}

export function RailCard({ eyebrow, title, aside, wide, children }: {
  eyebrow: string;
  title: string;
  aside?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`railpanel glass${wide ? " railwide" : ""}`}>
      <div className="hd">
        <div>
          <div className="eyebrow">{eyebrow}</div>
          <h2>{title}</h2>
        </div>
        {aside && <span className="u num">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];

// Monday-to-Sunday dates of the week containing `today`.
export function weekDates(today: string): string[] {
  const d = new Date(`${today}T00:00:00Z`);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(d);
    x.setUTCDate(x.getUTCDate() + i);
    return x.toISOString().slice(0, 10);
  });
}

// Seven panes, toggleable and backfillable within the current week. No
// streaks, nothing resets; a gap is just a gap.
export function WeekStrip({ planItemId, week, today }: {
  planItemId: string;
  week: string[];
  today: string;
}) {
  const toggleComplete = useStore((s) => s.toggleComplete);
  const dates = weekDates(today);
  return (
    <div className="week">
      {dates.map((date, i) => {
        const on = week.includes(date);
        const future = date > today;
        return (
          <button
            key={date}
            className={date === today ? "today" : ""}
            aria-pressed={on}
            aria-label={date === today ? "Today" : date}
            disabled={future}
            style={future ? { opacity: 0.35, cursor: "default" } : undefined}
            onClick={(ev) => {
              // The strip can sit inside a card that opens the practice
              // modal on click; a day toggle is never also a card tap.
              ev.stopPropagation();
              void toggleComplete(planItemId, date, !on);
            }}
          >
            <span className="bar" />
            <small>{DAY_LETTERS[i]}</small>
          </button>
        );
      })}
    </div>
  );
}

// One suggestion card: the metadata line, the practice, the why, three
// equal answers. No outbound links here; details live on the practice
// screen. The portrait is pinned to the top right whatever the metadata
// line does (29 Jul 2026): a wrapping line used to drag it down the card.
export function SugCard({ card, why }: { card: CardView; why: string }) {
  const openSheet = useStore((s) => s.openSheet);
  const openPopover = useStore((s) => s.openPopover);
  const e = useExpert(card.expertId ?? "");
  const at = (ev: React.MouseEvent) => {
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: r.right, y: r.bottom };
  };
  return (
    // The whole card opens the practice modal (reported 29 Jul 2026: the
    // chip row read as dead space). The action buttons stop propagation;
    // the body stays a real button so the keyboard path is unchanged.
    <div
      className="sug imgbg"
      style={{ ["--photo" as string]: artCss(artFor(card)) }}
      onClick={() => openSheet({ kind: "practice", cardId: card.id })}
    >
      <div className="top metatop">
        <MetaLine parts={[card.chip && <EvChip chip={card.chip} />, ...burdenParts(card.burden)]} />
        <span className="who">
          <Avatar e={e} size="s" title={avatarTitle(e)} />
        </span>
      </div>
      <button className="body" style={{ display: "block", width: "100%" }}>
        <span className="do" style={{ display: "block" }}>
          {card.do}
        </span>
        <span className="why" style={{ display: "block", marginTop: 7 }}>
          {why}
        </span>
      </button>
      <div className="acts2">
        {/* Add opens the practice modal (28 Jul 2026): the version choice
            and the committing Add button live there, one place for all
            surfaces. */}
        <button
          className="btn"
          onClick={(ev) => {
            ev.stopPropagation();
            openSheet({ kind: "practice", cardId: card.id });
          }}
        >
          Add
        </button>
        <button
          className="btn"
          onClick={(ev) => {
            ev.stopPropagation();
            const p = at(ev);
            openPopover({ kind: "snooze", cardId: card.id, ...p });
          }}
        >
          Later ▾
        </button>
        <button
          className="btn quiet"
          onClick={(ev) => {
            ev.stopPropagation();
            const p = at(ev);
            openPopover({ kind: "decline", cardId: card.id, ...p });
          }}
        >
          No thanks ▾
        </button>
      </div>
    </div>
  );
}

export function versionText(card: CardView, version: string): string {
  if (version === "minimal") return card.versionMinimal ?? card.versionStandard ?? card.do;
  if (version === "full") return card.versionFull ?? card.versionStandard ?? card.do;
  return card.versionStandard ?? card.versionMinimal ?? card.do;
}

// A practice row is titled by its action, never by its dose (29 Jul 2026).
// The version fields are dose variants, authored to be read as a choice
// list against the action ("20 minutes" / "20 to 30 minutes after lunch" /
// "Up to 45 minutes, scheduled"), and 1,295 of the 2,268 that exist are
// bare fragments that lose the verb standing alone: a check-list row read
// "1 to 3 mg per kg" or "20 to 30 minutes after lunch" and said nothing
// about what to do. The practice modal always led with the action and put
// the dose underneath it; the rows now agree with the modal.
//
// The dose is still worth showing once the user has moved off standard,
// because then it is their decision rather than the expert's default. It
// takes the burden time's place on the row's second line: same slot, more
// specific fact, and the line does not grow.
// The row's second line reads "expert · cadence · time", so a burden time that
// repeats the cadence word for word says it twice: "Ongoing · Ongoing", on 27
// cards. Drop the duplicate, not the fact.
export function rowTime(card: CardView): string {
  const time = readBurden(card.burden).time;
  const cadence = card.cadence ?? "Daily";
  return time.trim().toLowerCase() === cadence.trim().toLowerCase() ? "" : time;
}

export function doseNote(card: CardView, version: string): string {
  if (version === "standard") return "";
  const dose = versionText(card, version);
  return dose === card.do || dose === card.versionStandard ? "" : dose;
}
